import { type RefObject, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { type TaskScheduler } from '@air/classes-task-scheduler';
import {
  clearUploadInstabilityDetectedAction,
  isFinishedUploadingSelector,
  queuedUploadSizesSelector,
  totalInFlightBytesSelector,
  uploaderIsUploadingSelector,
  uploadInstabilityDetectedSelector,
  uploadingSpeedSelector,
  uploadLogger,
  useUploaderStore,
} from '@air/redux-uploader';

import {
  CONCURRENCY_BUDGET_MULTIPLIER,
  getUploadConcurrencyForSpeed,
  MAX_UPLOAD_CONCURRENCY,
  MIN_UPLOAD_CONCURRENCY,
} from './getUploadConcurrencyForSpeed';
import { type UploadConcurrencyChangeParams } from './uploadConcurrencyTypes';

const CONCURRENCY_CHECK_INTERVAL_MS = 1000;

type UseAdaptiveUploadConcurrencyParams = {
  onConcurrencyChange?: (params: UploadConcurrencyChangeParams) => void;
  schedulerRef: RefObject<TaskScheduler | undefined>;
};

type ConcurrencyMode = 'fixed' | 'adaptive';

export const useAdaptiveUploadConcurrency = ({
  onConcurrencyChange,
  schedulerRef,
}: UseAdaptiveUploadConcurrencyParams) => {
  const store = useUploaderStore();
  const dispatch = useDispatch();
  const isFinished = useSelector(isFinishedUploadingSelector);
  const isUploading = useSelector(uploaderIsUploadingSelector);
  const uploadInstabilityDetected = useSelector(uploadInstabilityDetectedSelector);

  const concurrencyRef = useRef<number>(MAX_UPLOAD_CONCURRENCY);
  const modeRef = useRef<ConcurrencyMode>('fixed');
  const onConcurrencyChangeRef = useRef(onConcurrencyChange);
  onConcurrencyChangeRef.current = onConcurrencyChange;

  // Initialise scheduler to fixed-5 on mount.
  useEffect(() => {
    const concurrency = MAX_UPLOAD_CONCURRENCY;
    modeRef.current = 'fixed';
    concurrencyRef.current = concurrency;
    schedulerRef.current?.setNumberOfWorkers(concurrency);
    uploadLogger.concurrencyInit({ workers: concurrency });
  }, [schedulerRef]);

  // React to upload instability detection: switch from fixed → adaptive.
  useEffect(() => {
    if (!uploadInstabilityDetected || modeRef.current === 'adaptive') {
      return;
    }

    const fromWorkers = concurrencyRef.current;
    const state = store.getState();
    const speed = uploadingSpeedSelector(state);
    const inFlightBytes = totalInFlightBytesSelector(state);
    const activeWorkers = schedulerRef.current?.getActiveWorkerCount() ?? 0;
    const budgetBytes = speed ? CONCURRENCY_BUDGET_MULTIPLIER * speed : undefined;

    modeRef.current = 'adaptive';
    concurrencyRef.current = MIN_UPLOAD_CONCURRENCY;
    schedulerRef.current?.setNumberOfWorkers(MIN_UPLOAD_CONCURRENCY);
    uploadLogger.concurrencyUploadInstabilityDetected({ workers: MIN_UPLOAD_CONCURRENCY });

    onConcurrencyChangeRef.current?.({
      active_workers: activeWorkers,
      change_reason: 'upload_instability_detected',
      concurrency_budget_bytes: budgetBytes,
      concurrency_mode: 'adaptive',
      from_workers: fromWorkers,
      in_flight_bytes: inFlightBytes,
      to_workers: MIN_UPLOAD_CONCURRENCY,
      upload_speed_bytes_per_second: speed ?? undefined,
    });
  }, [uploadInstabilityDetected, schedulerRef, store]);

  // Adaptive interval: only runs in adaptive mode while uploading.
  useEffect(() => {
    if (!isUploading) {
      return;
    }

    const intervalId = window.setInterval(() => {
      if (modeRef.current !== 'adaptive') {
        return;
      }

      const state = store.getState();
      const speed = uploadingSpeedSelector(state);
      const inFlightBytes = totalInFlightBytesSelector(state);
      const queuedUploadSizes = queuedUploadSizesSelector(state);
      const activeWorkers = schedulerRef.current?.getActiveWorkerCount() ?? 0;
      const current = concurrencyRef.current;
      const concurrency = getUploadConcurrencyForSpeed({
        currentConcurrency: current,
        inFlightBytes,
        queuedUploadSizes,
        speed,
      });

      const budgetBytes = speed ? CONCURRENCY_BUDGET_MULTIPLIER * speed : undefined;

      if (current !== concurrency) {
        schedulerRef.current?.setNumberOfWorkers(concurrency);
        concurrencyRef.current = concurrency;
        onConcurrencyChangeRef.current?.({
          active_workers: activeWorkers,
          change_reason: 'speed_adjustment',
          concurrency_budget_bytes: budgetBytes,
          concurrency_mode: 'adaptive',
          from_workers: current,
          in_flight_bytes: inFlightBytes,
          to_workers: concurrency,
          upload_speed_bytes_per_second: speed ?? undefined,
        });
      }

      uploadLogger.concurrencyAdjusted({
        activeWorkers,
        budget: budgetBytes ?? 0,
        from: current,
        inFlightBytes,
        queuedUploadSizes,
        speed,
        to: concurrency,
      });

      // Network recovered: adaptive reached max — switch back to fixed.
      if (concurrency === MAX_UPLOAD_CONCURRENCY) {
        // Capture before mutating modeRef so from_workers is accurate.
        const fromWorkers = concurrencyRef.current;
        modeRef.current = 'fixed';
        dispatch(clearUploadInstabilityDetectedAction());
        uploadLogger.concurrencyRevertedToFixed();
        onConcurrencyChangeRef.current?.({
          active_workers: activeWorkers,
          change_reason: 'reverted_to_fixed',
          concurrency_budget_bytes: budgetBytes,
          concurrency_mode: 'fixed',
          from_workers: fromWorkers,
          in_flight_bytes: inFlightBytes,
          to_workers: MAX_UPLOAD_CONCURRENCY,
          upload_speed_bytes_per_second: speed ?? undefined,
        });
      }
    }, CONCURRENCY_CHECK_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, [dispatch, isUploading, schedulerRef, store]);

  useEffect(() => {
    if (!isFinished) {
      return;
    }

    if (modeRef.current === 'adaptive') {
      concurrencyRef.current = MIN_UPLOAD_CONCURRENCY;
      schedulerRef.current?.setNumberOfWorkers(MIN_UPLOAD_CONCURRENCY);
    }
  }, [isFinished, schedulerRef]);
};
